import type { AlgoliaCompanyRecord } from '../../types/algolia-company-record';
import type { BestCompaniesLocale } from '../seo/best-companies-meta';
import { getCombinedRemoteHybridPercentForDisplay } from '../data/get-combined-remote-hybrid-percent-for-display';
import type { HiringTrend } from '../employer-score/types';

/** Sub-scores that currently contribute to the published employer composite. */
type SubKey = 'pay' | 'flexibility' | 'stability' | 'growth';

function pickDisplayName(company: AlgoliaCompanyRecord): string {
  return (company.companyName ?? '').trim() || company.objectID;
}

function subScores(company: AlgoliaCompanyRecord): Array<{ key: SubKey; value: number }> {
  const rows: Array<{ key: SubKey; value: number | undefined }> = [
    { key: 'pay', value: company.payScore },
    { key: 'flexibility', value: company.flexibilityScore },
    { key: 'stability', value: company.stabilityScore },
    { key: 'growth', value: company.growthScore },
  ];
  return rows
    .filter((r): r is { key: SubKey; value: number } => r.value != null && !Number.isNaN(r.value))
    .map((r) => ({ key: r.key, value: r.value as number }));
}

/** Strengths with sub-score ≥ threshold (0–1 scale). */
export function pickTopStrengthKeys(company: AlgoliaCompanyRecord, min = 0.52, max = 2): SubKey[] {
  return subScores(company)
    .filter((s) => s.value >= min)
    .sort((a, b) => b.value - a.value)
    .slice(0, max)
    .map((s) => s.key);
}

function formatSalaryBandEur(company: AlgoliaCompanyRecord): string | null {
  const min = company.medianSalaryMin;
  const max = company.medianSalaryMax;
  if (min == null || max == null || min <= 0 || max <= 0) return null;
  const lo = Math.round(min / 100) * 100;
  const hi = Math.round(max / 100) * 100;
  if (lo === hi) return `€${lo.toLocaleString('en-US')}`;
  return `€${lo.toLocaleString('en-US')}–€${hi.toLocaleString('en-US')}`;
}

function trendPhraseEn(trend: HiringTrend | undefined): string | null {
  if (trend === 'growing') return 'Hiring volume in their ads has been trending up recently.';
  if (trend === 'declining') return 'Hiring volume in their ads has softened recently.';
  if (trend === 'stable') return 'Hiring volume in their ads has been relatively steady.';
  return null;
}

function trendPhraseFi(trend: HiringTrend | undefined): string | null {
  if (trend === 'growing') return 'Ilmoitusvolyymi on ollut viime aikoina kasvussa.';
  if (trend === 'declining') return 'Ilmoitusvolyymi on ollut viime aikoina laskeva.';
  if (trend === 'stable') return 'Ilmoitusvolyymi on ollut suhteellisen vakaa.';
  return null;
}

function trendPhraseSv(trend: HiringTrend | undefined): string | null {
  if (trend === 'growing') return 'Annonsvolymen har trendat uppåt på sistone.';
  if (trend === 'declining') return 'Annonsvolymen har avtagit nyligen.';
  if (trend === 'stable') return 'Annonsvolymen har varit relativt stabil.';
  return null;
}

function strengthLabelsEn(keys: SubKey[]): string {
  const map: Record<SubKey, string> = {
    pay: 'salary band signals in ads',
    flexibility: 'remote and hybrid options in postings',
    stability: 'consistent hiring month to month',
    growth: 'growth of hiring activity',
  };
  if (keys.length === 0)
    return 'signals are fairly balanced across pay, flexibility, hiring stability, and growth';
  if (keys.length === 1) return `relative strength is in ${map[keys[0]!]}`;
  return `strongest listing signals are ${map[keys[0]!]} and ${map[keys[1]!]}`;
}

function strengthLabelsFi(keys: SubKey[]): string {
  const map: Record<SubKey, string> = {
    pay: 'palkka‑ ja palkkavaihtelu ilmoituksissa',
    flexibility: 'etä‑ ja hybridimahdollisuudet ilmoituksissa',
    stability: 'tasainen rekrytointivolyymi kuukausittain',
    growth: 'rekrytoinnin kasvuvauhti',
  };
  if (keys.length === 0)
    return 'signaalit jakautuvat tasaisesti palkan, joustavuuden, vakauden ja kasvun välillä';
  if (keys.length === 1) return `vahvin signaali on ${map[keys[0]!]}`;
  return `vahvimmat signaalit ovat ${map[keys[0]!]} ja ${map[keys[1]!]}`;
}

function strengthLabelsSv(keys: SubKey[]): string {
  const map: Record<SubKey, string> = {
    pay: 'löneintervall och lönesignaler i annonser',
    flexibility: 'distans- och hybridalternativ i annonser',
    stability: 'jämn rekryteringsvolym månad för månad',
    growth: 'tillväxt i rekryteringsaktivitet',
  };
  if (keys.length === 0)
    return 'signalerna är jämnt spridda över lön, flexibilitet, stabilitet och tillväxt';
  if (keys.length === 1) return `tydligast syns ${map[keys[0]!]}`;
  return `tydligast syns ${map[keys[0]!]} och ${map[keys[1]!]}`;
}

/**
 * One or two sentences explaining how JobCrawls rated this employer on the best-companies hub,
 * from listing-derived signals (not reviews). Intended under each row; locale-aware.
 */
export function buildBestCompaniesListingRatingExplainer(
  company: AlgoliaCompanyRecord,
  locale: BestCompaniesLocale,
): string {
  const name = pickDisplayName(company);
  const keys = pickTopStrengthKeys(company);
  const salary = formatSalaryBandEur(company);
  const remotePct = getCombinedRemoteHybridPercentForDisplay(company);
  const trend = company.hiringTrend as HiringTrend | undefined;
  const score = company.employerScore;
  const scoreInt = score != null ? Math.round(score) : null;

  if (locale === 'fi') {
    const parts: string[] = [];
    if (scoreInt != null) {
      parts.push(
        `JobCrawls pisteyttää työnantajan ${name} arvolla ${scoreInt}/100 Suomen työmarkkinalle suunnatuista työpaikkailmoituksista (ei työntekijäarvosteluja).`,
      );
    } else {
      parts.push(
        `JobCrawls pisteyttää työnantajan ${name} Suomen työmarkkinalle suunnatuista työpaikkailmoituksista (ei työntekijäarvosteluja).`,
      );
    }
    parts.push(`Tässä tapauksessa ${strengthLabelsFi(keys)}.`);
    const tr = trendPhraseFi(trend);
    if (tr) parts.push(tr);
    if (salary) parts.push(`Ilmoituksiin perustuva mediaanipalkka-alue: ${salary}/kk.`);
    if (remotePct != null) parts.push(`Etä- tai hybridityön osuus ilmoituksista: noin ${remotePct} %.`);
    return parts.join(' ');
  }

  if (locale === 'sv') {
    const parts: string[] = [];
    if (scoreInt != null) {
      parts.push(
        `JobCrawls arbetsgivarpoäng för ${name} är ${scoreInt}/100 utifrån platsannonser mot den finska arbetsmarknaden (inte medarbetaromdömen).`,
      );
    } else {
      parts.push(
        `JobCrawls bedömer ${name} utifrån platsannonser mot den finska arbetsmarknaden (inte medarbetaromdömen).`,
      );
    }
    parts.push(`Här är ${strengthLabelsSv(keys)}.`);
    const tr = trendPhraseSv(trend);
    if (tr) parts.push(tr);
    if (salary) parts.push(`Medianlöneintervall från annonser: ${salary}/mån.`);
    if (remotePct != null) parts.push(`Andel distans- eller hybridannonser: cirka ${remotePct} %.`);
    return parts.join(' ');
  }

  const parts: string[] = [];
  if (scoreInt != null) {
    parts.push(
      `JobCrawls rates ${name} at ${scoreInt}/100 using aggregated job listings aimed at the Finnish market—not employee reviews.`,
    );
  } else {
    parts.push(
      `JobCrawls ranks ${name} from aggregated job listings aimed at the Finnish market—not employee reviews.`,
    );
  }
  parts.push(`For this employer, ${strengthLabelsEn(keys)}.`);
  const tr = trendPhraseEn(trend);
  if (tr) parts.push(tr);
  if (salary) parts.push(`Median salary band from postings: ${salary}/month.`);
  if (remotePct != null) parts.push(`About ${remotePct}% of tracked postings are remote or hybrid.`);
  return parts.join(' ');
}
